import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ProjectService } from '../resources/project/project.service';
import { Building } from '../resources/building/building.interface';
import { Cluster } from '../resources/cluster/cluster.interface';
import { Area } from '../resources/area/area.interface';
import { ElementDragDrop, ProjectElement } from '../resources/element/project-element.interface';
import { Project } from '../resources/project/project.interface';
import { ElementLogs, ElementCommentCounts, ElementComment } from '../resources/log/logs.interface';
import { UserService } from '../resources/user/user.service';
import { BasicArea } from '../resources/basic-area/basic-area.interface';
import { ImageElementResponse } from '../services/attachment/attachment.interface';
import { ApiCoreService } from './api-core.service';

/**
 * Api Service.
 *
 * Handles api requests for all V2 API endpoints.
 *
 * @todo Split up info multiple files?
 *  - Per resource?
 *  - Per request type?
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService extends ApiCoreService {

  constructor(
    protected httpClient: HttpClient,
    protected projectService: ProjectService,
    private userService: UserService
  ) {
    super(httpClient, projectService);
  }

  /**
   * Add area.
   *
   * @param area Template for creating new area.
   * @returns The new area.
   */
  public addArea(area: Partial<Area>): Observable<Area> {
    const url = `
      /building
      /${ area.buildingId }
      /cluster
      /${ area.clusterId }
      /area`;
    return this._post(url, {
      area: area
    });
  }

  /**
   * Add cluster.
   *
   * @param building The parent building.
   * @param cluster Cluster template.
   * @returns Response as observable (whole project).
   */
  public addCluster(building: Building, cluster: Partial<Cluster>): Observable<Cluster> {
    const url = `
      /building
      /${ building._id }
      /cluster`;
    return this._post<Cluster>(url, {
      cluster: {
        name: cluster.name,
        shortCode: cluster.shortCode,
        ratio: cluster.ratio
      }
    });
  }

  /**
   * Add comment to an element.
   *
   * @todo Check return type.
   * @todo Check return type in reference method.
   *
   * @param element The element to add the comment for.
   * @param comment The comments as text.
   * @returns New element comment.
   */
  public addElementComment(element: ProjectElement, comment: ElementComment['text']): Observable<ElementComment> {
    const url = `
      /${ element.moduleId }
      /${ element.submoduleId }
      /${ element._id }
      /${ element.constructionId }
      /comment`;
    return this._post<ElementComment>(url, {
      text: comment
    });
  }

  /**
   * Delete area.
   *
   * @todo "Unknown api route".
   *
   * @param area The area to delete.
   * @returns Whether the area is deleted.
   */
  public deleteArea(area: Area): Observable<{}> {
    const url = `
      /building
      /${ area.buildingId }
      /cluster
      /${ area.clusterId }
      /area
      /${ area._id }`;
    return this._delete(url);
  }

  /**
   * Delete building.
   *
   * @param building The building to delete.
   * @returns Empty observable on success.
   */
  public deleteBuilding(building: Building): Observable<{}> {
    const url = `
      /building
      /${ building._id }`;
    return this._delete(url);
  }

  /**
   * Delete area template.
   *
   * @param areaTemplate The area template to delete.
   * @returns Empty observable on success.
   */
  public deleteAreaTemplate(areaTemplate: BasicArea): Observable<{}> {
    const url = `
      /areatemplate
      /${ areaTemplate._id }`;
    return this._delete(url);
  }

  /**
   * Delete cluster.
   *
   * @todo Why is response empty on success?
   *
   * @param cluster The cluster to delete.
   * @returns Whether the cluster is deleted.
   */
  public deleteCluster(cluster: Cluster): Observable<{}> {
    const url = `
      /building
      /${ cluster.buildingId }
      /cluster
      /${ cluster._id }`;
    return this._delete(url);
  }

  /**
   * Get element comment count.
   *
   * @param [projectId] Optional project id, for use in resolver.
   * @returns Comment counts summary.
   */
  public getElementCommentCount(): Observable<ElementCommentCounts> {
    return this._get<ElementCommentCounts>('/comment-count');
  }

  /**
   * Get logs for element.
   *
   * @param element The element to get the logs for.
   * @returns Element logs.
   */
  public getElementLogs(element: ProjectElement): Observable<ElementLogs> {
    const url = `
      /${ element.moduleId }
      /${ element.submoduleId }
      /${ element._id }
      /${ element.constructionId }
      /changes`;
    return this._get<ElementLogs>(url);
  }

  /**
   * Get project.
   *
   * Gets the current project, for use in the resolver.
   *
   * @todo Get project by specific id.
   *
   * @returns Project.
   */
  public getProject(): Observable<Project> {
    return this._get<Project>();
  }

  /**
   * Get project changes.
   *
   * @returns Element logs/changes.
   */
  public getProjectLogs(): Observable<ElementLogs> {
    return this._get<ElementLogs>('/changes');
  }

  /**
   * Move element.
   *
   * @param element The element to move.
   * @param target Target drag-drop location.
   * @returns Whether the move succeeded.
   */
  public moveElement(element: ProjectElement, location: ElementDragDrop): Observable<{}> {
    const url = `
      /element
      /${ element._id }
      /${ element.constructionId }
      /move`;
    return this._patch<any>(url, {
      source: {
        moduleId: element.moduleId,
        submoduleId: element.submoduleId,
        elementId: element._id,
        constructionId: element.constructionId
      },
      target: {
        moduleId: location.target.moduleId,
        submoduleId: location.target.submoduleId,
        elementId: location.target._id,
        constructionId: location.target.constructionId
      },
      position: location.position
    });
  }

  /**
   * Update area.
   *
   * @param area The area (current).
   * @param values Values to update.
   * @returns Updated area.
   */
  public updateArea(area: Area, values: Partial<Building>): Observable<Area> {
    const url = `
      /building
      /${ area.buildingId }
      /cluster
      /${ area.clusterId }
      /area
      /${ area._id }`;
    return this._patch<Area>(url, {
      ...values
    });
  }

  /**
   * Update area template.
   *
   * @param areaTemplate The area template to update.
   * @param values Values to update.
   * @returns The updated area template.
   */
  public updateAreaTemplate(areaTemplate: BasicArea, values: Partial<BasicArea>): Observable<BasicArea> {
    const url = `
      /areatemplate
      /${ areaTemplate._id }`;
    return this._patch<BasicArea>(url, {
      ...values
    });
  }

  /**
   * Update basic area quality level.
   *
   * @param areaTemplate The area template to update.
   * @param qualityLevel The quality level to set.
   * @returns ???.
   */
  public updateBasicAreaQualityLevel(areaTemplate: BasicArea, qualityLevel: BasicArea['qualityLevel']): any {
    const url = `
      /areatemplate
      /${ areaTemplate._id }
      /qualitylevel`;
    return this._patch<BasicArea>(url, {
      qualityLevel: qualityLevel
    });
  }

  /**
   * Update building.
   *
   * @param building The building (current).
   * @param values Values to update.
   * @returns Updated building.
   */
  public updateBuilding(building: Building, values: Partial<Building>): Observable<Building> {
    const url = `
      /building
      /${ building._id }`;
    return this._patch<Building>(url, {
      ...values
    });
  }

  /**
   * Update cluster.
   *
   * @param cluster The cluster (current).
   * @param values Values to update.
   * @returns Updated cluster.
   */
  public updateCluster(cluster: Cluster, values: Partial<Cluster>): Observable<Cluster> {
    const url = `
      /building
      /${ cluster.buildingId }
      /cluster
      /${ cluster._id }`;
    return this._patch<Cluster>(url, {
      ...values
    });
  }

  /**
   * Upload element image.
   *
   * @param filename The image's filename.
   * @param blob The file as a base64 blob.
   * @returns Image location.
   */
  public uploadElementImage(filename: string, blob: string): Observable<ImageElementResponse> {
    return this._post<ImageElementResponse>('/element/image', {
      base64: blob,
      createdBy: this.userService.getId(),
      filename: filename
    });
  }
}
